const text = value => (value == null ? '' : String(value).trim())

// Use the calendar day written in the source, without timezone conversion.
// Datetimes are accepted only with a valid ISO-like date/time shape.
export function recordedDay (value) {
  const raw = text(value)
  const match =
    /^(\d{4}-\d{2}-\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2})(?:\.\d{1,9})?)?(Z|[+-]\d{2}:\d{2})?)?$/.exec(
      raw
    )
  if (!match) return null
  const time = Date.parse(match[1] + 'T00:00:00Z')
  if (
    !Number.isFinite(time) ||
    new Date(time).toISOString().slice(0, 10) !== match[1]
  )
    return null
  if (
    match[2] &&
    (Number(match[2]) > 23 ||
      Number(match[3]) > 59 ||
      Number(match[4] || 0) > 59)
  )
    return null
  const zone = match[5]
  if (zone && zone !== 'Z') {
    const hours = Number(zone.slice(1, 3)),
      minutes = Number(zone.slice(4))
    if (hours > 14 || minutes > 59 || (hours === 14 && minutes !== 0))
      return null
  }
  return match[1]
}
export function historyAnomalyKey (row, companyId) {
  return text(row.id)
    ? JSON.stringify([text(companyId), 'anomaly', text(row.id)])
    : JSON.stringify([
        text(companyId),
        'anomaly-without-id',
        text(row.site_id),
        text(row.asset_id),
        text(row.anomaly_ref),
        text(row.anomaly_report_id),
        text(row.rds_component),
        text(row.raised_at)
      ])
}
export function normalizeHistoryAnomalies (value, companyId) {
  if (!Array.isArray(value))
    throw new Error('The anomaly API must return a list.')
  const seen = new Set()
  const rows = []
  let excluded = 0
  value.forEach(raw => {
    if (!raw || typeof raw !== 'object' || Array.isArray(raw))
      throw new Error('The anomaly list contains an invalid record.')
    if (raw.company_id != null && text(raw.company_id) !== text(companyId)) {
      excluded++
      return
    }
    const id = historyAnomalyKey(raw, companyId)
    if (seen.has(id))
      throw new Error(
        'Anomaly records have ambiguous identities; the history list cannot identify them uniquely.'
      )
    seen.add(id)
    rows.push({
      id,
      raw,
      reference: text(raw.anomaly_ref) || 'Reference not recorded',
      siteId: text(raw.site_id),
      assetId: text(raw.asset_id),
      component: text(raw.rds_component),
      description: text(raw.rds_description),
      techniques: (Array.isArray(raw.inspection_technique)
        ? raw.inspection_technique
        : [raw.inspection_technique]
      )
        .map(text)
        .filter(Boolean),
      severity: text(raw.anomaly_severity) || 'Not recorded',
      currentStatus: text(raw.open_closed) || 'Not recorded',
      day: recordedDay(raw.raised_at),
      rawDate: text(raw.raised_at),
      kind: 'anomaly'
    })
  })
  return { rows, excluded }
}
export function reportHistoryRows (reports) {
  const seen = new Set()
  return reports.map(report => {
    if (seen.has(report.key))
      throw new Error('Report records have duplicate identities.')
    seen.add(report.key)
    return {
      id: report.key,
      report,
      reference: report.number || 'Report without reference',
      siteId: report.siteId,
      assetId: report.assetId,
      siteLabel: report.siteLabel,
      assetLabel: report.assetLabel,
      type: report.type,
      techniques: report.techniques,
      day: recordedDay(report.date),
      rawDate: report.date,
      kind: 'report'
    }
  })
}
export function historyInScope (row, siteId, assetId) {
  return (
    (!siteId || row.siteId === String(siteId)) &&
    (!assetId || row.assetId === String(assetId))
  )
}
export const historyLocationKey = (siteId, assetId, companyId) =>
  JSON.stringify([
    String(companyId || ''),
    String(siteId || ''),
    String(assetId || '')
  ])
export function readHistoryComponent (params, siteId, assetId, companyId) {
  const code = params.get('history.component') || ''
  const valid =
    !code ||
    Boolean(
      siteId &&
        assetId &&
        params.get('history.componentScope') ===
          historyLocationKey(siteId, assetId, companyId)
    )
  return { code, valid }
}
export const matchesHistoryCode = (row, code) =>
  !code || row.component.toLowerCase() === code.trim().toLowerCase()

export function readHistoryDates (params, prefix) {
  const from = params.get(prefix + '.from') || '',
    to = params.get(prefix + '.to') || ''
  const validDay = value =>
    /^\d{4}-\d{2}-\d{2}$/.test(value) && recordedDay(value) === value
  const error =
    (from && !validDay(from)) || (to && !validDay(to))
      ? 'Use valid YYYY-MM-DD date limits.'
      : from && to && from > to
      ? 'The start date must not be after the end date.'
      : ''
  const setting = params.get(prefix + '.undated')
  return {
    from,
    to,
    error,
    includeUndated: setting === 'true' || (setting === null && !from && !to)
  }
}
export function historyWithinDates (row, dates) {
  if (dates.error) return false
  if (!row.day) return dates.includeUndated
  return (
    (!dates.from || row.day >= dates.from) && (!dates.to || row.day <= dates.to)
  )
}
export const anomalyHistoryFilters = [
  {
    key: 'technique',
    label: 'Inspection technique',
    values: row => row.techniques
  },
  { key: 'severity', label: 'Current severity', values: row => [row.severity] },
  {
    key: 'status',
    label: 'Current open/closed',
    values: row => [row.currentStatus]
  },
  {
    key: 'code',
    label: 'RDS code',
    values: row => [row.component || 'Not recorded']
  }
]
export const reportHistoryFilters = [
  { key: 'type', label: 'Report type', values: row => [row.type] },
  {
    key: 'technique',
    label: 'Inspection technique',
    values: row => row.techniques
  }
]
export const historySearch = row =>
  [
    row.reference,
    row.component,
    row.description,
    row.siteId,
    row.assetId,
    row.siteLabel,
    row.assetLabel,
    row.type,
    ...row.techniques
  ]
    .filter(Boolean)
    .join(' ')
export function historyColumns (kind) {
  return [
    {
      key: 'reference',
      label: kind === 'anomaly' ? 'Anomaly reference' : 'Report number',
      value: row => row.reference
    },
    {
      key: 'day',
      label: kind === 'anomaly' ? 'Recorded date raised' : 'Report date',
      value: row => row.day
    },
    {
      key: 'location',
      label: 'Location',
      value: row =>
        [row.siteLabel || row.siteId, row.assetLabel || row.assetId].join(' / ')
    },
    kind === 'anomaly'
      ? {
          key: 'component',
          label: 'RDS component',
          value: row => row.component
        }
      : { key: 'type', label: 'Report type', value: row => row.type },
    {
      key: 'techniques',
      label: 'Technique',
      value: row => row.techniques.join(' · ')
    },
    ...(kind === 'anomaly'
      ? [
          {
            key: 'currentStatus',
            label: 'Current open/closed',
            value: row => row.currentStatus
          }
        ]
      : [])
  ]
}
export function sortHistory (rows, sort, columns) {
  const column = columns.find(item => item.key === sort.key) || columns[1]
  return [...rows].sort((a, b) => {
    const left = column.value(a),
      right = column.value(b)
    const leftMissing = left == null || left === '',
      rightMissing = right == null || right === ''
    if (leftMissing || rightMissing) {
      if (leftMissing !== rightMissing) return leftMissing ? 1 : -1
      return a.id.localeCompare(b.id)
    }
    return (
      (sort.direction === 'asc' ? 1 : -1) *
        String(left).localeCompare(String(right), undefined, {
          numeric: true
        }) || a.id.localeCompare(b.id)
    )
  })
}
